import React, { useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import CheckFormWrapper from '../shared/CheckFormWrapper';
import FileUploadField from '../shared/FileUploadField';
import ColWithValuesInput from '../shared/ColWithValuesInput';
import TagsInput from '../shared/TagsInput';

/**
 * GenericCheckForm
 *
 * Generic user-account check: filters by type / lock / validity date,
 * merges with an HR address file, and flags service / generic accounts.
 *
 * API: POST /helper/generic-check
 *
 * Props:
 *   onSubmit   - (FormData) => void
 *   isLoading  - bool
 */
export default function GenericCheckForm({ onSubmit, isLoading = false }) {
  const [inputFile, setInputFile] = useState(null);
  const [usrAddrFile, setUsrAddrFile] = useState(null);

  const [userNameCol, setUserNameCol] = useState([]);
  const [userNameColError, setUserNameColError] = useState('');

  const [userTypeCol, setUserTypeCol] = useState('');
  const [userTypeVals, setUserTypeVals] = useState([]);

  const [userLockCol, setUserLockCol] = useState('');
  const [userLockVals, setUserLockVals] = useState([]);

  const [dateColName, setDateColName] = useState('');
  const [dateColVal, setDateColVal] = useState('');

  const [employeeNameCol, setEmployeeNameCol] = useState([]);
  const [employeeNameColError, setEmployeeNameColError] = useState('');

  const [nameFilters, setNameFilters] = useState([]);
  const [requiredCol, setRequiredCol] = useState([]);

  const buildUserTypeCol = () =>
    userTypeCol ? [userTypeCol, ...userTypeVals] : [];

  const buildUserLockCol = () =>
    userLockCol ? [userLockCol, ...userLockVals] : [];

  const buildDateCol = () =>
    dateColName && dateColVal ? [dateColName, dateColVal] : [];

  const handleSubmit = (e) => {
    e.preventDefault();

    let ok = true;
    if (userNameCol.length === 0) {
      setUserNameColError('Add at least one column name');
      ok = false;
    } else {
      setUserNameColError('');
    }
    if (employeeNameCol.length === 0) {
      setEmployeeNameColError('Add at least one employee name column');
      ok = false;
    } else {
      setEmployeeNameColError('');
    }
    if (!ok) return;

    const formData = new FormData();
    formData.append('inputFile', inputFile);
    formData.append('usrAddrFile', usrAddrFile);
    formData.append('userNameCol', JSON.stringify(userNameCol));
    formData.append('userTypeCol', JSON.stringify(buildUserTypeCol()));
    formData.append('userLockCol', JSON.stringify(buildUserLockCol()));
    formData.append('dateCol', JSON.stringify(buildDateCol()));
    formData.append('employeeNameCol', JSON.stringify(employeeNameCol));
    formData.append('nameFilters', JSON.stringify(nameFilters));
    formData.append('requiredCol', JSON.stringify(requiredCol));

    onSubmit(formData);
  };

  return (
    <CheckFormWrapper
      title="Generic Check"
      description="Filters user accounts by type, lock status, and validity date, then merges with HR data to flag service/generic accounts."
      onSubmit={handleSubmit}
      isLoading={isLoading}
    >
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <FileUploadField
          label="User Account Extract"
          file={inputFile}
          onChange={setInputFile}
          required
          helperText=".csv or .xlsx"
        />
        <FileUploadField
          label="User Address / HR File"
          file={usrAddrFile}
          onChange={setUsrAddrFile}
          required
          helperText=".csv or .xlsx"
        />
      </Box>

      <Box>
        <TagsInput
          label={
            <>
              User Name Column <span style={{ color: '#d32f2f' }}>*</span>
            </>
          }
          values={userNameCol}
          onChange={(next) => {
            setUserNameCol(next);
            if (next.length > 0) setUserNameColError('');
          }}
          placeholder="Column name (e.g. UserID), then Enter or Add"
          helperText="First item = source column in the extract. Add a second item only if you use a separate merge column."
        />
        {userNameColError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: 'block', mt: 0.5 }}
          >
            {userNameColError}
          </Typography>
        )}
      </Box>

      <ColWithValuesInput
        label="User Type Filter (optional)"
        colLabel="User Type Column"
        valuesLabel="Allowed User Types"
        colValue={userTypeCol}
        values={userTypeVals}
        onColChange={setUserTypeCol}
        onValuesChange={setUserTypeVals}
        helperText="Leave empty to skip this filter"
      />

      <ColWithValuesInput
        label="User Lock Filter (optional)"
        colLabel="Lock Status Column"
        valuesLabel="Allowed Lock Values (integers)"
        colValue={userLockCol}
        values={userLockVals}
        onColChange={setUserLockCol}
        onValuesChange={setUserLockVals}
        valueType="number"
        helperText="Leave empty to skip this filter"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="body2" fontWeight={500}>
          Date Filter (optional)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Date Column"
            value={dateColName}
            onChange={(e) => setDateColName(e.target.value)}
            sx={{ minWidth: 220, maxWidth: 280 }}
          />
          <TextField
            size="small"
            label="Comparison value"
            value={dateColVal}
            onChange={(e) => setDateColVal(e.target.value)}
            sx={{ minWidth: 220, maxWidth: 280 }}
            helperText="Use the same format as in your file (e.g. 2024-06-01)"
          />
        </Box>
      </Box>

      <Box>
        <TagsInput
          label={
            <>
              Employee Name Columns <span style={{ color: '#d32f2f' }}>*</span>
            </>
          }
          values={employeeNameCol}
          onChange={(next) => {
            setEmployeeNameCol(next);
            if (next.length > 0) setEmployeeNameColError('');
          }}
          placeholder="Column name (e.g. First Name), then Enter or Add"
          helperText="First item = first-name column; add a second item for last-name column if needed."
        />
        {employeeNameColError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: 'block', mt: 0.5 }}
          >
            {employeeNameColError}
          </Typography>
        )}
      </Box>

      <TagsInput
        label="Extra Name Filters (optional)"
        values={nameFilters}
        onChange={setNameFilters}
        helperText="Additional keywords that flag an account as Ineffective"
      />

      <TagsInput
        label="Extra Columns to Include (optional)"
        values={requiredCol}
        onChange={setRequiredCol}
        helperText="Additional column names to carry through to the output"
      />
    </CheckFormWrapper>
  );
}
